import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../api/apiClient';
import './style.css';

const DEFAULT_LIMITATIONS = [
  'None',
  'Upper-body injury (shoulder/elbow/wrist)',
  'Lower-body injury (knee/ankle/hip)',
  'Back pain / history of back injury',
  'No overhead pressing',
  'No barbell lifts',
  'No running / jumping (low impact only)',
  'Limited equipment (dumbbells only)',
  'Limited equipment (bands/bodyweight only)',
];

const FOCUS_OPTIONS = [
  { value: 'ALL', label: 'All muscle groups' },
  { value: 'Chest', label: 'Chest' },
  { value: 'Shoulders', label: 'Shoulders' },
  { value: 'Back', label: 'Back' },
  { value: 'Quads', label: 'Quads' },
  { value: 'Hamstrings', label: 'Hamstrings' },
  { value: 'Glutes', label: 'Glutes' },
  { value: 'Arms', label: 'Arms' },
  { value: 'Core', label: 'Core' },
  { value: 'Lower Body Only', label: 'Lower Body Only' },
  { value: 'Upper Body Only', label: 'Upper Body Only' },
];

const LIMITATION_FOCUS_RULES = {
  'Upper-body injury (shoulder/elbow/wrist)': [
    'Chest',
    'Shoulders',
    'Back',
    'Arms',
    'Upper Body Only',
  ],
  'Lower-body injury (knee/ankle/hip)': ['Quads', 'Hamstrings', 'Glutes', 'Lower Body Only'],
  'Back pain / history of back injury': ['Back'],
  'No overhead pressing': ['Shoulders', 'Upper Body Only'],
};

const GOAL_OPTIONS = [
  { value: 'Muscle Size', label: 'Muscle size (hypertrophy)' },
  { value: 'Strength', label: 'Strength' },
  { value: 'Fat Loss', label: 'Fat loss / recomposition' },
  { value: 'Endurance', label: 'Endurance / conditioning' },
  { value: 'General Fitness', label: 'General fitness' },
];

const EXPERIENCE_OPTIONS = [
  {
    value: 'complete_beginner',
    title: 'Complete beginner',
    subtext: 'Just starting or new to working out',
  },
  {
    value: 'basic_familiarity',
    title: 'Basic familiarity',
    subtext: 'Used machines or beginner routines, some experience',
  },
  {
    value: 'intermediate',
    title: 'Intermediate trainer',
    subtext: 'Regularly lifting, comfortable with free weights and routines',
  },
  {
    value: 'advanced',
    title: 'Advanced lifter',
    subtext: 'Years of consistent training, experienced with advanced techniques',
  },
];

const TRAINING_DAY_OPTIONS = [2, 3, 4, 5];
const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FOCUS_ALL = 'ALL';
const DEFAULT_SPLIT_BLUEPRINTS = {
  2: {
    code: 'full_body_foundations_2d',
    title: 'Full Body Foundations (2-Day)',
    tagline: 'Two total-body sessions mixing compound lifts, accessories, and metabolic finishers.',
    section: 'foundations',
    focus_tags: ['full-body', 'balanced'],
    is_mixed: true,
    supports_days: [2],
  },
  3: {
    code: 'three_day_full_body_wave',
    title: '3-Day Full Body Wave',
    tagline: 'Alternating emphasis so every push, pull, and lower-body pattern gets trained each week.',
    section: 'foundations',
    focus_tags: ['full-body', 'balanced'],
    is_mixed: true,
    supports_days: [3],
  },
  4: {
    code: 'upper_lower_pillar',
    title: 'Upper / Lower Pillar Split',
    tagline: 'Two upper and two lower sessions layered with accessory work and core finishers.',
    section: 'intermediate',
    focus_tags: ['upper', 'lower'],
    is_upper: true,
    is_lower: true,
    is_mixed: true,
    supports_days: [4],
  },
  5: {
    code: 'push_pull_lower_hybrid',
    title: 'Push / Pull / Lower Hybrid (5-Day)',
    tagline: 'Five-day rotation blending strength, hypertrophy, recovery touch points, and finishers.',
    section: 'intermediate',
    focus_tags: ['push', 'pull', 'lower', 'hybrid'],
    is_mixed: true,
    supports_days: [5],
  },
};

const EXPERIENCE_NOTES = {
  complete_beginner: 'Built for beginners to cement movement patterns and confidence.',
  basic_familiarity: 'Volume stays approachable while reinforcing fundamentals.',
};

const clampTrainingDays = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 3;
  return Math.min(5, Math.max(2, num));
};

const uniqueSorted = (values = []) => Array.from(new Set(values)).sort((a, b) => a - b);

const hasFourConsecutive = (indices = []) => {
  if (indices.length < 4) return false;
  const sorted = uniqueSorted(indices);
  const extended = sorted.concat(sorted.map((idx) => idx + 7));
  let streak = 1;
  for (let i = 1; i < extended.length; i += 1) {
    if (extended[i] === extended[i - 1] + 1) {
      streak += 1;
      if (streak >= 4) return true;
    } else {
      streak = 1;
    }
  }
  return false;
};

const summarizeFocus = (selection = []) => {
  if (!selection.length || selection.includes(FOCUS_ALL)) return 'Balanced full-body focus';
  return `Prioritize: ${selection.join(', ')}`;
};

const splitPayload = (split) => {
  if (!split) return null;
  return {
    id: split.id,
    code: split.code,
    title: split.title,
    tagline: split.tagline,
    section: split.section,
    focus_tags: split.focus_tags,
    is_upper: split.is_upper,
    is_lower: split.is_lower,
    is_mixed: split.is_mixed,
    supports_days: split.supports_days,
  };
};

const stripFocusTags = (selection = []) =>
  selection.filter((value) => value && value !== FOCUS_ALL);

const ensureSplitSupportsDayCount = (split, dayCount) => {
  if (!split) return null;
  if (!Array.isArray(split.supports_days) || !split.supports_days.length) {
    return split;
  }
  return split.supports_days.includes(dayCount) ? split : null;
};

const createFallbackSplit = (dayCount, focusSelection = [], experienceLevel = null) => {
  const blueprint =
    DEFAULT_SPLIT_BLUEPRINTS[dayCount] ||
    DEFAULT_SPLIT_BLUEPRINTS[Math.min(Math.max(dayCount, 2), 5)] ||
    null;
  if (!blueprint) return null;
  const customFocus = stripFocusTags(focusSelection);
  const focusCopy = customFocus.length ? `Extra attention on ${customFocus.join(', ')}.` : '';
  const experienceCopy = EXPERIENCE_NOTES[experienceLevel] || '';
  const taglineParts = [blueprint.tagline, experienceCopy, focusCopy].filter(Boolean);
  return {
    ...blueprint,
    focus_tags: Array.from(new Set([...(blueprint.focus_tags || []), ...customFocus])),
    supports_days: blueprint.supports_days?.length ? blueprint.supports_days : [dayCount],
    tagline: taglineParts.join(' ').trim(),
  };
};

const toDayNames = (indices = []) =>
  uniqueSorted(indices).map((idx) => WEEK_DAYS[idx] || WEEK_DAYS[0]);

export default function TrainingDaysModal({
  open,
  initialDays = 5,
  initialGoal = '',
  includeGoalStep = false,
  onSave,
  onClose = () => {},
  canDismiss = false,
}) {
  const [stepIdx, setStepIdx] = useState(0);
  const [days, setDays] = useState(() => clampTrainingDays(initialDays));
  const [limits, setLimits] = useState([]);
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [focusSelection, setFocusSelection] = useState([FOCUS_ALL]);
  const [experience, setExperience] = useState(null);
  const [splitOptions, setSplitOptions] = useState([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitError, setSplitError] = useState('');
  const [selectedSplit, setSelectedSplit] = useState(null);
  const [preferredDays, setPreferredDays] = useState([]);
  const [scheduleError, setScheduleError] = useState('');
  const activeLimitations = useMemo(
    () => (limits || []).filter((item) => item && item !== 'None'),
    [limits]
  );
  const hasActiveLimitations = activeLimitations.length > 0;
  const focusRestriction = useMemo(() => {
    const blockedSet = new Set();
    activeLimitations.forEach((limit) => {
      const blockedValues = LIMITATION_FOCUS_RULES[limit];
      if (!blockedValues) return;
      blockedValues.forEach((value) => blockedSet.add(value));
    });
    const labels = FOCUS_OPTIONS.filter(
      (option) => option.value !== FOCUS_ALL && blockedSet.has(option.value)
    ).map((option) => option.label);
    return { set: blockedSet, labels };
  }, [activeLimitations]);

  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setLimits([]);
    setPrimaryGoal(initialGoal || 'Muscle Size');
    setFocusSelection([FOCUS_ALL]);
    setExperience(null);
    setSplitOptions([]);
    setSelectedSplit(null);
    setPreferredDays([]);
    setScheduleError('');
    setSplitError('');
  }, [open, initialGoal]);

  useEffect(() => {
    if (!open) return;
    setDays(clampTrainingDays(initialDays));
  }, [initialDays, open]);

  const focusFilters = useMemo(
    () => focusSelection.filter((item) => item !== FOCUS_ALL),
    [focusSelection]
  );

  useEffect(() => {
    setFocusSelection((prev) => {
      const filtered = prev.filter(
        (value) => value === FOCUS_ALL || !focusRestriction.set.has(value)
      );
      if (filtered.length === prev.length) return prev;
      return filtered.length ? filtered : [FOCUS_ALL];
    });
  }, [focusRestriction]);

  const focusKey = focusFilters.slice().sort().join('|');
  const shouldSkipSplit =
    experience === 'complete_beginner' || experience === 'basic_familiarity';
  const baseSteps = ['days', 'limitations', 'focus', 'experience', 'split', 'schedule'];
  const stepSequence = useMemo(() => {
    const seq = [...baseSteps];
    if (includeGoalStep) {
      seq.splice(2, 0, 'goal'); // after limitations
    }
    return seq;
  }, [includeGoalStep]);
  const visibleSteps = stepSequence.filter(
    (step) => step !== 'split' || !shouldSkipSplit
  );
  const currentStep = stepSequence[stepIdx];
  const questionNumber = Math.max(1, visibleSteps.indexOf(currentStep) + 1);
  const isFinalStep = currentStep === 'schedule';

  useEffect(() => {
    if (!open || shouldSkipSplit) return;
    let active = true;
    async function loadSplits() {
      setSplitLoading(true);
      setSplitError('');
      try {
        const params = new URLSearchParams();
        params.set('days', String(days));
        if (focusFilters.length) {
          params.set('focus', focusFilters.join(','));
        }
        const res = await apiFetch(`/workout-split-patterns?${params.toString()}`);
        let payload = null;
        try {
          payload = await res.json();
        } catch {
          payload = null;
        }
        if (!active) return;
        if (!res.ok) {
          throw new Error(payload?.error || 'Unable to load split options.');
        }
        setSplitOptions(Array.isArray(payload?.data) ? payload.data : []);
      } catch (err) {
        if (!active) return;
        setSplitOptions([]);
        setSplitError(err?.message || 'Unable to load split options.');
      } finally {
        if (active) setSplitLoading(false);
      }
    }
    loadSplits();
    return () => {
      active = false;
    };
  }, [days, focusKey, shouldSkipSplit, open, focusFilters]);

  useEffect(() => {
    if (shouldSkipSplit) {
      setSelectedSplit(null);
      setSplitOptions([]);
    }
  }, [shouldSkipSplit]);

  useEffect(() => {
    if (!selectedSplit || !splitOptions.length) return;
    const match = splitOptions.find((option) => option.code === selectedSplit.code);
    if (!match) {
      setSelectedSplit(null);
    } else if (match !== selectedSplit) {
      setSelectedSplit(match);
    }
  }, [splitOptions, selectedSplit]);

  const handleDayCountChange = (value) => {
    setDays(value);
    setPreferredDays((prev) => uniqueSorted(prev).slice(0, Math.min(prev.length, value)));
    setSelectedSplit((prev) => {
      if (!prev) return prev;
      if (Array.isArray(prev.supports_days) && !prev.supports_days.includes(value)) {
        return null;
      }
      return prev;
    });
    setScheduleError('');
  };

  const toggleLimit = (label) => {
    setLimits((prev) => {
      if (label === 'None') return ['None'];
      const next = new Set(prev.filter((item) => item !== 'None'));
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return Array.from(next);
    });
  };

  const toggleFocus = (value) => {
    setFocusSelection((prev) => {
      if (value === FOCUS_ALL) return [FOCUS_ALL];
      const next = new Set(prev.filter((item) => item !== FOCUS_ALL));
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next.size ? Array.from(next) : [FOCUS_ALL];
    });
  };

  const togglePreferredDay = (idx) => {
    setPreferredDays((prev) => {
      const exists = prev.includes(idx);
      if (exists) {
        setScheduleError('');
        return prev.filter((value) => value !== idx);
      }
      if (prev.length >= days) {
        setScheduleError(`Select exactly ${days} training days.`);
        return prev;
      }
      const next = uniqueSorted([...prev, idx]);
      if (hasFourConsecutive(next)) {
        setScheduleError('Keep at least one rest day after three training days.');
        return prev;
      }
      setScheduleError('');
      return next;
    });
  };

  const advanceStep = () => {
    if (isFinalStep) return;
    let nextIdx = Math.min(stepSequence.length - 1, stepIdx + 1);
    if (stepSequence[nextIdx] === 'split' && shouldSkipSplit) {
      nextIdx = Math.min(stepSequence.length - 1, nextIdx + 1);
    }
    setStepIdx(nextIdx);
  };

  const goBack = () => {
    if (stepIdx === 0) return;
    let prevIdx = Math.max(0, stepIdx - 1);
    if (stepSequence[prevIdx] === 'split' && shouldSkipSplit) {
      prevIdx = Math.max(0, prevIdx - 1);
    }
    setStepIdx(prevIdx);
  };

  const canProceed = useMemo(() => {
    if (currentStep === 'days') return days >= 2 && days <= 5;
    if (currentStep === 'goal') return Boolean(primaryGoal);
    if (currentStep === 'focus') return focusSelection.length > 0;
    if (currentStep === 'experience') return Boolean(experience);
    if (currentStep === 'split') {
      if (splitLoading) return false;
      return Boolean(selectedSplit) || splitOptions.length === 0;
    }
    if (currentStep === 'schedule') {
      return preferredDays.length === days;
    }
    return true;
  }, [
    currentStep,
    days,
    primaryGoal,
    focusSelection,
    experience,
    selectedSplit,
    splitOptions,
    splitLoading,
    preferredDays.length,
  ]);

  if (!open) return null;

  const questionCopy = {
    days: {
      title: 'Training Days (2-5)',
      lead: 'How many days per week can you realistically lift?',
    },
    limitations: {
      title: 'Any limitations or injuries?',
      lead: 'Select all that apply so we can tailor movements and volume.',
    },
    goal: {
      title: 'Primary goal',
      lead: 'What do you want to get out of this plan?',
    },
    focus: {
      title: 'Muscle group priorities',
      lead: 'Pick any areas you want to emphasize. Choose "All muscle groups" if you want a balanced plan.',
    },
    experience: {
      title: 'Strength training experience',
      lead: 'Tell us how comfortable you are with lifting so we can match volume and complexity.',
    },
    split: {
      title: 'Choose your workout split',
      lead: `Based on ${days} training day(s). ${summarizeFocus(focusSelection)}.`,
    },
    schedule: {
      title: 'Pick your training days',
      lead: `Choose exactly ${days} day(s) of the week. We will keep at least one rest day after any three training days.`,
    },
  };

  const handleNext = () => {
    if (!canProceed) return;
    if (isFinalStep) {
      const resolvedSplit =
        ensureSplitSupportsDayCount(selectedSplit, days) ||
        createFallbackSplit(days, focusSelection, experience);
      const payload = {
        days,
        primaryGoal,
        limitations: limits.length ? limits : ['None'],
        focusAreas: focusSelection,
        experienceLevel: experience,
        splitPreference: splitPayload(resolvedSplit),
        preferredDays: toDayNames(preferredDays),
      };
      onSave(payload);
      return;
    }
    advanceStep();
  };

  return (
    <div className="td-modal-overlay">
      <div className="td-modal" role="dialog" aria-modal="true" aria-labelledby="td-title">
        <div className="td-header">
          <div>
            <p className="td-step-label">
              Question {questionNumber} of {visibleSteps.length}
            </p>
            <h3 id="td-title">{questionCopy[currentStep]?.title}</h3>
          </div>
          {canDismiss && (
            <button type="button" className="td-close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        <div key={currentStep} className="td-body fade-in">
          <p className="td-lead">{questionCopy[currentStep]?.lead}</p>

          {currentStep === 'days' && (
            <>
              <div className="td-choices-vertical">
                {TRAINING_DAY_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`td-option ${days === n ? 'selected' : ''}`}
                    onClick={() => handleDayCountChange(n)}
                  >
                    {n} {n === 1 ? 'Day' : 'Days'}
                  </button>
                ))}
              </div>
              <p className="td-footnote">
                We will still enforce at least one rest day after any three consecutive training days.
              </p>
            </>
          )}

          {currentStep === 'limitations' && (
            <div className="td-scroll">
              {DEFAULT_LIMITATIONS.map((label) => {
                const selected =
                  limits.includes(label) || (label === 'None' && limits.length === 0);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`td-row ${selected ? 'selected' : ''}`}
                    onClick={() => toggleLimit(label)}
                  >
                    <span className="td-row-title">{label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {currentStep === 'goal' && (
            <div className="td-card-grid">
              {GOAL_OPTIONS.map((option) => {
                const selected = primaryGoal === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`td-card ${selected ? 'selected' : ''}`}
                    onClick={() => setPrimaryGoal(option.value)}
                  >
                    <div className="td-card-title">{option.label}</div>
                  </button>
                );
              })}
            </div>
          )}

          {currentStep === 'focus' && (
            <>
              {focusRestriction.labels.length > 0 && (
                <p className="td-footnote">
                  Some priorities are unavailable with the selected limitations:{' '}
                  {focusRestriction.labels.join(', ')}.
                </p>
              )}
              <div className="td-chip-grid">
                {FOCUS_OPTIONS.map((option) => {
                  const selected = focusSelection.includes(option.value);
                  const disabled =
                    option.value !== FOCUS_ALL && focusRestriction.set.has(option.value);
                  const displayLabel =
                    option.value === FOCUS_ALL && hasActiveLimitations
                      ? 'All Available Muscle Groups'
                      : option.label;
                  const chipClass = [
                    'td-chip',
                    selected ? 'selected' : '',
                    disabled ? 'disabled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={chipClass}
                      disabled={disabled}
                      aria-disabled={disabled}
                    onClick={() => {
                      if (!disabled) toggleFocus(option.value);
                    }}
                  >
                    {displayLabel}
                  </button>
                );
              })}
            </div>
            </>
          )}

          {currentStep === 'experience' && (
            <div className="td-card-grid">
              {EXPERIENCE_OPTIONS.map((option) => {
                const selected = experience === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`td-card ${selected ? 'selected' : ''}`}
                    onClick={() => setExperience(option.value)}
                  >
                    <div className="td-card-title">{option.title}</div>
                    <div className="td-card-subtext">{option.subtext}</div>
                  </button>
                );
              })}
            </div>
          )}

          {currentStep === 'split' && (
            <>
              {splitLoading && <div className="td-loading">Loading split options...</div>}
              {!splitLoading && splitOptions.length === 0 && !splitError && (
                <p className="td-footnote">
                  No exact split matches were found for those filters. Continue and we will still tailor the plan.
                </p>
              )}
              {splitError && <p className="td-error">{splitError}</p>}
              <div className="td-card-grid">
                {splitOptions.map((option) => {
                  const selected = selectedSplit?.code === option.code;
                  return (
                    <button
                      key={option.code}
                      type="button"
                      className={`td-card ${selected ? 'selected' : ''}`}
                      onClick={() => setSelectedSplit(option)}
                    >
                      <div className="td-card-title">{option.title}</div>
                      {option.tagline && <div className="td-card-subtext">{option.tagline}</div>}
                      {Array.isArray(option.focus_tags) && option.focus_tags.length > 0 && (
                        <div className="td-card-tags">
                          {option.focus_tags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {currentStep === 'schedule' && (
            <>
              <p className="td-footnote">
                Selected {preferredDays.length} of {days} day(s).
              </p>
              <div className="td-day-grid">
                {WEEK_DAYS.map((label, index) => {
                  const selected = preferredDays.includes(index);
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`td-day ${selected ? 'selected' : ''}`}
                      onClick={() => togglePreferredDay(index)}
                    >
                      {label.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
              {scheduleError && <p className="td-error">{scheduleError}</p>}
            </>
          )}
        </div>

        <div className="td-actions">
          {stepIdx > 0 && (
            <button type="button" className="btn-ghost" onClick={goBack}>
              Back
            </button>
          )}
          <button
            type="button"
            className="td-btn-primary td-next"
            disabled={!canProceed}
            onClick={handleNext}
          >
            {isFinalStep ? 'Build My Plan' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
